/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { WidgetId } from 'features/types'

import WordCloudChart from '../../../../visualization/WordCloud'
import { useDashboardWidgetData } from '../../../../../hooks/useWidgetState'
import { useDashboardWidgetSetting } from '../../../../../hooks/useDashboardState'
import { getWordCloudData } from './utils'
import PlaceHolder from '../../../../share/PlaceHolder'
import { useEmojiNameToDisplayMap } from '../../../../../hooks/useEmojiState'
import { WordCloudWidgetsType, isSentimentDriversWidget, WordCloudSelectionType } from '../../types'
import { InsightCount } from '../../../../../schemas/action/InsightCountSchema'

// NI-2615: `@netbasequid/word-cloud` (d3-cloud fork) has an internal race in
// `cloudSprite()` / `place()` where a re-layout under CB3's parallel
// runSearches storm can read `tag.sprite` after a previous run already
// `delete`d it (word-cloud.esm.js:2693 — `sprite[j * w + i]` on undefined).
// The crash is caught by this boundary, but the previous fallback rendered
// the raw `error.message` ("Cannot read properties of undefined (reading
// '0')") inside `<PlaceHolder>`, which surfaced English library text in the
// widget. Show the same friendly i18n empty-state copy that the upstream
// `WordCloudEmptyResultGuard` uses, and log the original error so we don't
// lose diagnostics on the library bug.
export const WordCloudFallback = ({ error }: FallbackProps) => {
  const { t } = useTranslation()
  // eslint-disable-next-line no-console
  console.error('NI-2615 WordCloud render crash', error)
  return <PlaceHolder>{t('noDataFoundInWidget')}</PlaceHolder>
}

// TODO: split this widget into 3 widgets: WordCloudWidget, SentimentDriverWidget, PhotoConceptWidget
const WordCloud: ({
  widgetId,
  onClick
}: {
  widgetId: WidgetId
  onClick?: (target: WordCloudSelectionType) => void
}) => React.ReactElement = ({ widgetId, onClick }) => {
  const widgetSetting = useDashboardWidgetSetting<WordCloudWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()

  const data = getWordCloudData(widgetData, widgetSetting, emojiNameToDisplayMap)

  return (
    <ErrorBoundary FallbackComponent={WordCloudFallback}>
      <WordCloudChart
        data={data}
        color={isSentimentDriversWidget(widgetSetting) ? undefined : widgetSetting.color}
        onClick={onClick}
      />
    </ErrorBoundary>
  )
}

export default WordCloud
